/** 萬事通能力文案：只依 status.capabilities 呈現已接查詢，不宣稱供應商或全面官方查核。 */

export const CAP_OFFICIAL_WEATHER = 'official_weather';
export const CAP_OFFICIAL_SOURCE_LOOKUP = 'official_source_lookup';
export const CAP_WEB_SEARCH = 'web_search';
export const READONLY_SOURCE_KINDS = ['official_weather', 'weather_guidance', 'official_tax'];
export const SOURCE_KIND_WEB_SEARCH = 'web_search';
const KNOWN_SOURCE_KINDS = new Set([...READONLY_SOURCE_KINDS, SOURCE_KIND_WEB_SEARCH]);
const KNOWN_SEARCH_STATUSES = new Set(['complete', 'partial']);
const KNOWN_READ_STATUSES = new Set(['full', 'opened', 'snippet', 'partial', 'unknown']);
const SOURCE_VERIFICATIONS = new Set(['provider_citation', 'opened_page', 'unverified_link']);
const VERIFICATION_LABELS = {
  provider_citation: '搜尋結果標示的來源',
  opened_page: '已開啟該頁',
  unverified_link: '僅出現在回覆文字中的連結，尚未核對全文',
};

function capabilitySet(status) {
  const list = status && Array.isArray(status.capabilities) ? status.capabilities : [];
  return new Set(list.filter((name) => typeof name === 'string'));
}

export function hasAssistantCapability(status, name) {
  return capabilitySet(status).has(name);
}

function connectedSourceLabels(caps) {
  const labels = [];
  if (caps.has(CAP_OFFICIAL_WEATHER)) labels.push('天氣預報');
  if (caps.has(CAP_OFFICIAL_SOURCE_LOOKUP)) labels.push('台北房屋稅');
  return labels;
}

function lookupBlurb(labels, canWebSearch) {
  const official = labels.length
    ? `已接來源的${labels.join('與')}會附來源。`
    : '目前未顯示已接的官方即時查詢。';
  const web = canWebSearch ? '可搜尋網路。搜尋完成不代表每個全文都已讀取。' : '';
  return `${official}${web}其他即時資料未查證會明說。`;
}

export function assistantCapabilityCopy(status) {
  const caps = capabilitySet(status);
  const labels = connectedSourceLabels(caps);
  const lookup = lookupBlurb(labels, caps.has(CAP_WEB_SEARCH));
  const history = status?.conversation_history?.persistent === false
    ? '同一對話可連續追問；伺服器端上下文僅暫存在目前副本，重啟、部署或切換副本時可能失效。瀏覽器仍保留畫面紀錄。'
    : '同一對話可連續追問；若伺服器上下文失效，請補充前文。';
  const intro = `我可以聊天、說明，也可以查公司車輛／商機。${lookup}要新增或更新公司資料時會先出草稿給你確認。外站整批爬取匯入仍不支援，請用搜房 CSV 匯入。`;
  const rules = [
    ['對話', `一般問題盡量直接回答，不必只限公司資料。${history}`],
    ['外部查詢', lookup],
    ['公司資料', '可查車輛／商機等組織資料；沒有的會明說，不編造。'],
    ['寫入', '新增／更新單筆資料會先出草稿，確認後才寫入。純天氣或官方稅率回覆不需要草稿。'],
    ['不做', '不爬外站整批匯入、不批次亂改、不跨公司操作、不代發訊給客戶。'],
  ];
  return { intro, rules };
}

export function assistantCapabilityCopyText(status) {
  const { intro, rules } = assistantCapabilityCopy(status);
  return [intro, ...rules.map(([key, value]) => `${key}${value}`)].join('\n');
}

export function isReadOnlyOfficialResult(result) {
  if (!result || typeof result !== 'object') return false;
  return READONLY_SOURCE_KINDS.includes(result.sourceKind);
}

export function isNativeSearchResult(result) {
  if (!result || typeof result !== 'object') return false;
  return result.sourceKind === SOURCE_KIND_WEB_SEARCH;
}

export function requiresDraftConfirm(result) {
  if (isReadOnlyOfficialResult(result) || isNativeSearchResult(result)) return false;
  return !!(result && result.draft);
}

function isPublicIpv4(hostname) {
  const parts = hostname.split('.').map(Number);
  if (parts.length !== 4 || parts.some((part) => !Number.isInteger(part) || part < 0 || part > 255)) return false;
  const [a, b, c] = parts;
  if (a === 0 || a === 10 || a === 127 || a >= 224) return false;
  if (a === 100 && b >= 64 && b <= 127) return false;
  if (a === 169 && b === 254) return false;
  if (a === 172 && b >= 16 && b <= 31) return false;
  if (a === 192 && b === 168) return false;
  if (a === 192 && b === 0 && (c === 0 || c === 2)) return false;
  if (a === 192 && b === 88 && c === 99) return false;
  if (a === 198 && (b === 18 || b === 19 || (b === 51 && c === 100))) return false;
  if (a === 203 && b === 0 && c === 113) return false;
  return true;
}

function isIpv4Hostname(hostname) {
  return /^\d{1,3}(?:\.\d{1,3}){3}$/.test(hostname);
}

function isIpv6Hostname(hostname) {
  return hostname.includes(':');
}

function isPublicIpv6(hostname) {
  const host = hostname.replace(/^\[|\]$/g, '').toLowerCase();
  if (host === '::' || host === '::1') return false;
  if (host.startsWith('fc') || host.startsWith('fd') || host.startsWith('ff')) return false;
  if (/^fe[89ab]/.test(host)) return false;
  if (host.startsWith('2001:db8:') || host === '2001:db8::') return false;
  if (host.includes('::ffff:')) return false;
  return true;
}

function isPublicHostname(hostname) {
  const host = String(hostname || '').replace(/^\[|\]$/g, '').toLowerCase().replace(/\.$/, '');
  if (!host) return false;
  if (isIpv4Hostname(host)) return isPublicIpv4(host);
  if (isIpv6Hostname(host)) return isPublicIpv6(host);
  if (!host.includes('.')) return false;
  if (host === 'localhost' || host === 'metadata.google.internal') return false;
  if (/(?:^|\.)(?:localhost|local|internal|lan|home|corp)$/.test(host)) return false;
  return true;
}

function removeProviderFragment(parsed) {
  if (!parsed.hash || !/ws_call_id/i.test(parsed.hash)) return;
  const fragment = parsed.hash.slice(1);
  const kept = fragment
    .split('&')
    .filter((part) => !/^ws_call_id(?:=|$)/i.test(part));
  parsed.hash = kept.length ? `#${kept.join('&')}` : '';
}

function parsePublicWebUrl(value) {
  if (typeof value !== 'string' || !value.trim()) return null;
  try {
    const parsed = new URL(value.trim());
    if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return null;
    if (parsed.username || parsed.password || !isPublicHostname(parsed.hostname)) return null;
    removeProviderFragment(parsed);
    return parsed;
  } catch {
    return null;
  }
}

export function sanitizeWebSources(input) {
  if (!Array.isArray(input)) return [];
  const candidates = input
    .map((item, index) => {
      if (!item || typeof item !== 'object' || Array.isArray(item)) return null;
      const parsed = parsePublicWebUrl(item.url);
      if (!parsed) return null;
      const title = typeof item.title === 'string' && item.title.trim()
        ? item.title.trim().slice(0, 300)
        : parsed.hostname.slice(0, 300);
      const source = { title, url: parsed.toString() };
      if (SOURCE_VERIFICATIONS.has(item.verification)) source.verification = item.verification;
      const dedupeKey = `${parsed.hostname.toLowerCase()}|${parsed.port}|${parsed.pathname}${parsed.search}|${parsed.hash}`;
      return { source, dedupeKey, secure: parsed.protocol === 'https:', index };
    })
    .filter(Boolean)
    .sort((a, b) => Number(b.secure) - Number(a.secure) || a.index - b.index);

  const seen = new Set();
  const output = [];
  for (const candidate of candidates) {
    if (seen.has(candidate.dedupeKey)) continue;
    seen.add(candidate.dedupeKey);
    output.push(candidate.source);
    if (output.length === 5) break;
  }
  return output;
}

export function verificationLabel(verification) {
  return VERIFICATION_LABELS[verification] || '';
}

export function formatSearchedAt(iso) {
  if (typeof iso !== 'string' || !iso.trim()) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Taipei',
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);
  const get = (type) => parts.find((part) => part.type === type)?.value;
  const month = Number(get('month'));
  const day = Number(get('day'));
  const hour = String(get('hour') || '').padStart(2, '0');
  const minute = String(get('minute') || '').padStart(2, '0');
  if (!get('year') || !month || !day) return '';
  return `${get('year')}/${month}/${day} ${hour}:${minute}`;
}

function knownIso(value) {
  if (typeof value !== 'string' || !value.trim()) return undefined;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return undefined;
  return date.toISOString();
}

export function searchMetaCopy(message) {
  if (!message || message.sourceKind !== SOURCE_KIND_WEB_SEARCH) return '';
  if (!KNOWN_SEARCH_STATUSES.has(message.searchStatus)) return '';
  const bits = [];
  if (message.searchStatus === 'complete') {
    bits.push('已完成搜尋。搜尋完成不代表每個全文都已讀取');
  } else {
    bits.push('搜尋只完成一部分，部分來源可能尚未讀取全文');
  }
  const webSources = Array.isArray(message.webSources) ? message.webSources : [];
  if (!webSources.length) bits.push('這次搜尋沒有可安全顯示的來源');
  if (message.readStatus === 'full') bits.push('已取得可驗證的全文內容');
  else if (message.readStatus === 'opened') bits.push('已開啟要求頁面，但這不代表已取得完整全文');
  else if (message.readStatus === 'partial') bits.push('只讀到部分要求頁面');
  else if (message.readStatus === 'snippet') bits.push('目前只有搜尋摘要，未證明已讀全文');
  else if (message.readStatus === 'unknown') bits.push('目前無法確認頁面內容覆蓋程度');
  const when = formatSearchedAt(message.searchedAt);
  if (when) bits.push(when);
  return bits.join(' · ');
}

export function chatItemsFromResult(result) {
  if (!result || typeof result !== 'object') return [];
  const items = [];
  if (result.reply) {
    const item = {
      role: 'bot',
      text: result.reply,
      sources: result.sources,
      webSources: sanitizeWebSources(result.web_sources),
    };
    if (KNOWN_SOURCE_KINDS.has(result.sourceKind)) item.sourceKind = result.sourceKind;
    if (item.sourceKind === SOURCE_KIND_WEB_SEARCH) {
      if (KNOWN_SEARCH_STATUSES.has(result.search_status)) item.searchStatus = result.search_status;
      if (KNOWN_READ_STATUSES.has(result.read_status)) item.readStatus = result.read_status;
      const searchedAt = knownIso(result.searched_at);
      if (searchedAt) item.searchedAt = searchedAt;
    }
    if (result.history_status?.reset === true) {
      item.contextNotice = '伺服器端對話上下文已失效，本次從新的上下文開始。';
    }
    items.push(item);
  }
  if (requiresDraftConfirm(result) && result.draft) {
    items.push({ role: 'draft', draft: result.draft });
  }
  return items;
}
